import {
  Alert,
  InteractiveContainer,
  PlainButton,
  Tooltip,
  TextAreaInput,
} from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { COLLECT_LEADS_TOOLTIP_SHOWN_KEY } from "@jugl-web/utils/storage";
import React, { useCallback, useEffect, useState, ReactNode } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { DEFAULT_WHEN_TO_COLLECT_LEAD } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import Lottie from "react-lottie";
import { CollectLeadsSettingsDialog } from "./components/CollectLeadsSettingsDialog";
import { ReactComponent as ResetIcon } from "./assets/reset.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import brainAnimation from "./assets/brain.json";

export const WhenToCollectLeadPanel: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const [message, setMessage] = useState("");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [wasCollectLeadsTooltipShown, setWasCollectLeadsTooltipShown] =
    useLocalStorage(COLLECT_LEADS_TOOLTIP_SHOWN_KEY, false);
  const { data } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const isDefaultMessage =
    message.trim() === DEFAULT_WHEN_TO_COLLECT_LEAD.trim();

  useEffect(() => {
    setMessage(data?.config.when_to_collect_lead || "");
  }, [data]);

  useEffect(() => {
    if (
      data &&
      !!data.config.when_to_collect_lead &&
      !wasCollectLeadsTooltipShown
    ) {
      setIsTooltipOpen(true);
    }
  }, [data, wasCollectLeadsTooltipShown]);

  const dismissCollectLeadsTooltip = useCallback(() => {
    setIsTooltipOpen(false);
    setWasCollectLeadsTooltipShown(true);
  }, [setWasCollectLeadsTooltipShown]);

  const persistCollectLeadConfig = useCallback(
    async (nextMessage: string) => {
      const trimmedMessage = nextMessage.trim();
      setMessage(trimmedMessage);

      const isDirty = trimmedMessage !== data?.config.when_to_collect_lead;
      if (!isDirty || !data) return;

      try {
        await updateAgentConfig({
          entityId,
          config: {
            ...data.config,
            when_to_collect_lead: trimmedMessage,
          },
        }).unwrap();
      } catch {
        // Error UI handled via meta + apiResultMiddleware
      }
    },
    [data, entityId, updateAgentConfig]
  );

  return (
    <div className="mx-8 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="font-secondary font-medium text-black">
          {t({
            id: "cx-ai-agents-page.collect-leads",
            defaultMessage: "🗒️ Collect Leads",
          })}
        </span>
        <div className="flex items-center gap-2">
          {!isDefaultMessage && (
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                  ref={ref}
                  {...props}
                  onClick={() => {
                    setIsResetConfirmOpen(true);
                    dismissCollectLeadsTooltip();
                  }}
                >
                  <ResetIcon />
                </PlainButton>
              )}
            >
              {t({
                id: "cx-ai-agents-page.reset-to-default-when-to-collect-lead-prompt",
                defaultMessage: "Reset to default prompt",
              })}
            </Tooltip>
          )}
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                ref={ref}
                {...props}
                onClick={() => {
                  setIsSettingsOpen(true);
                  dismissCollectLeadsTooltip();
                }}
              >
                <SettingsIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "cx-ai-agents-page.collect-leads-settings-tooltip",
              defaultMessage: "Collect Leads settings",
            })}
          </Tooltip>
        </div>
      </div>
      <Tooltip
        isOpen={isTooltipOpen}
        placement="right"
        className="w-[360px] max-w-full rounded-[20px] bg-[#333333] p-0"
        zIndex={10}
        renderTrigger={({ props, ref }) => (
          <TextAreaInput
            ref={ref}
            value={message}
            onChange={(e) => setMessage(e.target.value.trimStart())}
            onBlur={() => persistCollectLeadConfig(message)}
            placeholder={t({
              id: "cx-ai-agents-page.when-collect-lead-placeholder",
              defaultMessage:
                "When the customer requests to talk to a human representative, or when the customer's issue cannot be resolved through automated assistance.",
            })}
            classNames={{
              inputClassName: cx(
                "font-secondary text-sm leading-[140%] text-black jugl__custom-scrollbar h-[200px] min-h-[200px] max-h-[200px]"
              ),
            }}
            {...props}
          />
        )}
      >
        <div className="flex items-center justify-between gap-2 border-0 border-b border-solid border-dark-800 py-[3px] pl-6">
          <span className="font-secondary text-lg leading-[140%] font-semibold text-white">
            {t({
              id: "cx-ai-agents-page.get-customers-automated-title",
              defaultMessage: "Get Customers automated",
            })}
          </span>
          <Lottie
            options={{ animationData: brainAnimation }}
            width={78}
            height={78}
          />
        </div>
        <div className="px-6 py-2.5">
          <span className="font-secondary text-sm leading-[21px] text-white">
            {t(
              {
                id: "cx-ai-agents-page.collect-leads-instructions-info",
                defaultMessage:
                  "Define when the AI Agent should ask for customer details. New leads will be saved to Customers automatically. Configure fields in <highlightedText>Settings</highlightedText>",
              },
              {
                highlightedText: (text: ReactNode) => (
                  <span className="bg-gradient-to-r from-[#ADC7FF] to-[#CD9AFF] bg-clip-text font-semibold text-transparent">
                    {text}
                  </span>
                ),
              }
            )}
          </span>
        </div>
        <div className="flex h-10 items-center justify-end">
          <InteractiveContainer
            className="mx-8 mb-6 bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text font-secondary text-sm leading-3 font-semibold text-transparent uppercase"
            onClick={dismissCollectLeadsTooltip}
          >
            {t({
              id: "common.okay",
              defaultMessage: "Okay",
            })}
          </InteractiveContainer>
        </div>
      </Tooltip>
      <Alert
        isOpen={isResetConfirmOpen}
        onRequestClose={() => setIsResetConfirmOpen(false)}
        title={t({
          id: "cx-ai-agents-page.reset-when-to-collect-lead-dialog-title",
          defaultMessage: "Reset to default",
        })}
        content={t(
          {
            id: "cx-ai-agents-page.reset-when-to-collect-lead-dialog-description",
            defaultMessage:
              "Are you sure you want to reset to the default <highlightedText>Collect Leads</highlightedText> prompt?",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {text}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            style: { flex: "none", width: "140px" },
          },
          {
            text: t({
              id: "common.reset",
              defaultMessage: "Reset",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: (_, { closeAlert }) => {
              closeAlert();
              persistCollectLeadConfig(DEFAULT_WHEN_TO_COLLECT_LEAD);
            },
          },
        ]}
      />
      <CollectLeadsSettingsDialog
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        entityId={entityId}
      />
    </div>
  );
};
